import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { Button } from 'src/components/atoms/Button';
import { useContactForm } from 'src/hooks/useContactForm';

const fieldStyles = {
  bg: 'white',
  color: 'text.darkBlue.800',
  borderColor: 'gray.300',
  borderRadius: 'md',
  _placeholder: { color: 'gray.500' },
  _hover: { borderColor: 'border.box' },
  _focus: {
    borderColor: 'blue.500',
    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
  },
};

type ContactFormData = {
  name: string;
  email: string;
  company: string;
  message: string;
};

type ContactFormProps = {
  onSubmit?: (data: ContactFormData) => Promise<void>;
  onClose?: () => void;
  defaultMessage?: string;
  title?: string;
  submitButtonText?: string;
};

export const ContactForm: React.FC<ContactFormProps> = ({
  onSubmit,
  onClose,
  defaultMessage = "We're happy to chat about pricing, enterprise plans, or set up a personalized demo.",
  title,
  submitButtonText = 'Send Message',
}) => {
  const { submitContactForm, isLoading } = useContactForm();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      company: '',
      message: defaultMessage,
    },
  });

  const isDefaultMessage = watch('message') === defaultMessage;

  const handleFormSubmit = async (data: ContactFormData) => {
    if (onSubmit) {
      await onSubmit(data);
    } else {
      const result = await submitContactForm(data);
      if (result.success) {
        reset();
        onClose?.();
      }
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.name}>
          <FormLabel htmlFor="name" fontSize="base" fontWeight="medium">
            Name *
          </FormLabel>
          <Input
            {...register('name', { required: 'Name is required' })}
            id="name"
            placeholder="John Doe"
            {...fieldStyles}
          />
          {errors.name && (
            <FormErrorMessage>{errors.name.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.email}>
          <FormLabel htmlFor="email" fontSize="base" fontWeight="medium">
            Email *
          </FormLabel>
          <Input
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            id="email"
            type="email"
            placeholder="john.doe@company.com"
            {...fieldStyles}
          />
          {errors.email && (
            <FormErrorMessage>{errors.email.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.company}>
          <FormLabel htmlFor="company" fontSize="base" fontWeight="medium">
            Company *
          </FormLabel>
          <Input
            {...register('company', { required: 'Company name is required' })}
            id="company"
            placeholder="Your Company"
            {...fieldStyles}
          />
          {errors.company && (
            <FormErrorMessage>{errors.company.message}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={!!errors.message}>
          <FormLabel htmlFor="message" fontSize="base" fontWeight="medium">
            Message *
          </FormLabel>
          <Textarea
            {...register('message', { required: 'Message is required' })}
            id="message"
            placeholder="Tell us about your needs..."
            {...fieldStyles}
            color={isDefaultMessage ? 'gray.500' : fieldStyles.color}
            rows={4}
          />
          {errors.message && (
            <FormErrorMessage>{errors.message.message}</FormErrorMessage>
          )}
        </FormControl>

        <Box display="flex" justifyContent="center" pt={4}>
          <Button
            buttonName="contact-form-submit"
            type="submit"
            size="lg"
            bg={'icon.green'}
            color="white"
            fontFamily="marketingPage"
            borderRadius={8}
            px={8}
            py={4}
            isLoading={isLoading}
            loadingText="Sending..."
            _hover={{ bg: 'green.600' }}>
            {submitButtonText} &nbsp; →
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};
